import React, { useContext, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { DragDropContext } from 'react-beautiful-dnd'
import { TimelineContext } from '../../../../../contexts/TimelineContext'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import TimelineSidebar from './TimelineSidebar'
import TimelineHeader from './TimelineHeader'
import TimelineCalander from './TimelineCalander'
import TimelineChangeDatesModal from './TimelineChangeDatesModal'
import { toastError } from '../../../../FormComponents/Toasts'
import TimelinePageLayout from '../../../../Shared/TimelineParts/TimelinePageLayout'

export default observer(function TimelineContent() {
  const [activeSidebarTab, setActiveSidebarTab] = useState('timeline')
  const { timelineState } = useContext(TimelineContext)
  const { campaign, projectAssets, campaignScenarios } = useContext(CampaignContext)

  const campaignScenario = campaignScenarios[0]
  const toDateOnly = (value) => {
    if (!value) return null
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  }
  const campaignStartDate = toDateOnly(campaign?.start_date)
  const campaignEndDate = toDateOnly(campaign?.end_date)

  /** react-beautiful-dnd droppable ids for calendar columns are `date-YYYY-MM-DD`. */
  const dateStrFromDroppableId = (droppableId) => {
    if (!droppableId || !droppableId.startsWith('date-')) return null
    return droppableId.slice('date-'.length)
  }

  const isValidYmd = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

  // Track cursor X during drag so sidebar drops can use the actual cursor
  // position instead of react-beautiful-dnd's center-based detection.
  // Sidebar items are full-width (~270px), so their element center is far
  // from where the cursor is; using cursor X directly gives accurate results.
  const lastCursorX = useRef(0)
  const moveSub = useRef(null)

  const onDragStart = () => {
    timelineState.setDragging(true)
    const onMove = (e) => { lastCursorX.current = e.clientX }
    document.addEventListener('mousemove', onMove)
    moveSub.current = onMove
  }

  const onDragEnd = (result) => {
    timelineState.setDragging(false)
    if (moveSub.current) {
      document.removeEventListener('mousemove', moveSub.current)
      moveSub.current = null
    }

    const { source, destination, draggableId } = result
    if (!destination) return

    // For sidebar items use the actual cursor position to find the calendar
    // column, bypassing react-beautiful-dnd's center-based drop detection
    // which is skewed by the sidebar item's full width.
    let targetDate
    const isSidebarDrag = draggableId.startsWith('project-asset-') || draggableId.startsWith('action-')
    if (isSidebarDrag) {
      const cursorX = lastCursorX.current
      let found = null
      document.querySelectorAll('[data-rbd-droppable-id^="date-"]').forEach(el => {
        const rect = el.getBoundingClientRect()
        if (cursorX >= rect.left && cursorX <= rect.right) {
          found = dateStrFromDroppableId(el.getAttribute('data-rbd-droppable-id') || '')
        }
      })
      // Only trust RBD destination when it is a calendar column (drops on the
      // sidebar list use droppableId `project-assets`; splitting that yields
      // garbage like `assets` instead of a date).
      targetDate = found || dateStrFromDroppableId(destination.droppableId)
    } else {
      targetDate = dateStrFromDroppableId(destination.droppableId)
    }

    if (draggableId.startsWith('timeline-')) {
      // Moving existing timeline item
      const actionId = draggableId.replace('timeline-', '')
      const sourceDate = dateStrFromDroppableId(source.droppableId)

      // Get the actual asset from the timeline items
      if (!sourceDate || !isValidYmd(targetDate)) return

      const sourceItems = timelineState.timelineItems.get(sourceDate) || []
      const movedAsset = sourceItems.find(item => String(item.id) === String(actionId))

      if (movedAsset) {
        // Calculate the number of days between start and end dates
        const startDate = new Date(movedAsset.start_date)
        const endDate = new Date(movedAsset.end_date)
        const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))

        // Calculate new end date by adding the same number of days to the target date
        const newStartDate = new Date(targetDate)
        const newEndDate = new Date(newStartDate)
        newEndDate.setDate(newEndDate.getDate() + daysDifference)

        if ((campaignStartDate && newStartDate < campaignStartDate) || (campaignEndDate && newEndDate > campaignEndDate)) {
          toastError('Asset dates must stay within the campaign period.')
          return
        }

        timelineState.updateTimelineItemDate(
          movedAsset,
          sourceDate,
          targetDate,
          newEndDate.toISOString().split('T')[0],
          campaignScenario
        )
      }
    } else if (draggableId.startsWith('project-asset-')) {
      // New item from project assets sidebar
      if (!isValidYmd(targetDate)) return

      const assetId = draggableId.replace('project-asset-', '')
      const targetStartDate = toDateOnly(targetDate)
      if ((campaignStartDate && targetStartDate < campaignStartDate) || (campaignEndDate && targetStartDate > campaignEndDate)) {
        toastError('Asset start date must be within the campaign period.')
        return
      }
      timelineState.addProjectAssetToTimeline(projectAssets, campaignScenario, assetId, targetDate)
    } else if (draggableId.startsWith('action-')) {
      // New item from scenario assets sidebar
      if (!isValidYmd(targetDate)) return

      const actionId = draggableId.replace('action-', '')
      const targetStartDate = toDateOnly(targetDate)
      if ((campaignStartDate && targetStartDate < campaignStartDate) || (campaignEndDate && targetStartDate > campaignEndDate)) {
        toastError('Asset start date must be within the campaign period.')
        return
      }
      timelineState.addActionToTimeline(campaignScenario, actionId, targetDate)
    }
  }

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <TimelinePageLayout
        header={
          <TimelineHeader
            activeTab={activeSidebarTab}
            onTabChange={setActiveSidebarTab}
          />
        }
        sidebar={<TimelineSidebar activeTab={activeSidebarTab} />}
        calendar={<TimelineCalander />}
      >
        <TimelineChangeDatesModal
          isOpen={timelineState.showChangeDatesModal}
          onClose={() => timelineState.toggleChangeDatesModal()}
        />
      </TimelinePageLayout>
    </DragDropContext>
  )
})